import { Autocomplete, TextField, useTheme } from "@mui/material";
import * as React from "react";

import { actionImageError, isActionImageValid } from "@/shared/domain/actions";
import { useImagesByOrg } from "@/shared/domain/actions/hooks";
import { OrgRecord } from "@/shared/domain/orgs";

type ImageDetails = {
  registry: string;
  repository: string;
  imageName: string;
  tag: string;
};

/**
 * Parses a Docker image string into its components.
 *
 * Expected format: <registry>/<repository>/<imageName>:<tag>
 * Example: "123456789012.dkr.ecr.us-west-2.amazonaws.com/myrepo/myimage:latest"
 *   - registry: "123456789012.dkr.ecr.us-west-2.amazonaws.com"
 *   - repository: "myrepo"
 *   - imageName: "myimage"
 *   - tag: "latest"
 *
 * Invalid example: "myimage:latest" → missing registry and repository
 */
function parseDockerImage(image: string): ImageDetails {
  const regex =
    /^(?<registry>[^/]+)\/(?<repository>.+?)\/(?<imageName>[^:]+):(?<tag>.+)$/;
  const match = image.match(regex);

  if (!match?.groups) {
    throw new Error("Invalid Docker image format.");
  }

  const { registry, repository, imageName, tag } = match.groups;

  return { registry, repository, imageName, tag };
}

interface ImageSelectorProps {
  image: string | undefined;
  onSelectImage: (imageUri: string | undefined) => void;
  currentOrg: OrgRecord;
}

export const ImageSelector: React.FC<ImageSelectorProps> = ({
  image,
  onSelectImage,
  currentOrg,
}) => {
  const theme = useTheme();

  const images = useImagesByOrg(currentOrg.org_id);

  return (
    <Autocomplete
      id="available-images"
      fullWidth
      freeSolo
      size={"small"}
      loading={images.isLoading}
      groupBy={() => "Your Private Registry Images"}
      options={(images.data?.items ?? []).map((img) => img.image_uri)}
      value={image}
      renderOption={(props, option) => {
        let image;
        try {
          image = parseDockerImage(option);
        } catch {
          image = {
            imageName: option,
            tag: "",
            registry: "",
            repository: "",
          };
        }

        return (
          <li style={{ display: "block" }} {...props}>
            <>
              <div>{`${image.imageName}:${image.tag}`}</div>
              <span
                style={{
                  fontSize: "0.7rem",
                  display: "block",
                  color: theme.palette.text.secondary,
                }}
              >
                {`${image.registry}/${image.repository}`}
              </span>
            </>
          </li>
        );
      }}
      onChange={(_event, newValue) => {
        onSelectImage(newValue ?? undefined);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Enter an image that can be pulled with Docker"
          error={actionImageError(image)}
          helperText={
            image === undefined ||
            image === "" ||
            (image && isActionImageValid(image))
              ? ""
              : "Image must include registry, repository, name, and tag (e.g., 123456789012.dkr.ecr.us-west-2.amazonaws.com/myrepo/myimage:latest)"
          }
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            onSelectImage(event.target.value);
          }}
        />
      )}
      componentsProps={{
        paper: {
          sx: {
            fontSize: "0.875rem",
          },
        },
      }}
    />
  );
};
